import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Auth, Plots, Worlds, api, errorMessage } from './api/client';
import { AuthUser, PlotRef, PlotSizeGroup, World, isStaffUser } from './types';
import { UsernamesProvider } from './hooks/useUsernames';
import { useTheme } from './hooks/useTheme';
import { plotUrl } from './utils/plots';
import Login from './components/Login';
import Rail, { RailView } from './components/Rail';
import Home from './components/Home';
import WorldPage from './components/WorldPage';
import NoWorld from './components/NoWorld';
import Admin from './components/Admin';
import PlotsPage from './components/PlotsPage';
import PlotPage from './components/PlotPage';
import FeedbackDialog from './components/FeedbackDialog';

const isDev = process.env.NODE_ENV === 'development';

/** Route target for a world page; keeps the current tab when already on that world. */
const WorldRoute: React.FC<{ worlds: World[]; loading: boolean; me: AuthUser; onWorldsRefresh: () => void }> = ({ worlds, loading, me, onWorldsRefresh }) => {
  const { worldId } = useParams<{ worldId: string }>();
  const world = worlds.find((w) => w.id === worldId);
  if (!world && loading) {
    return (
      <div className="main2">
        <div className="center" style={{ paddingTop: 120 }}>
          <span className="spin" /> Loading…
        </div>
      </div>
    );
  }
  if (!world) return <NoWorld notFound />;
  return <WorldPage key={world.id} world={world} me={me} onWorldsRefresh={onWorldsRefresh} />;
};

/** Route target for a plot page; the plot comes from the already-loaded "Your plots" list. */
const PlotRoute: React.FC<{ sizes: PlotSizeGroup[] | null; loading: boolean }> = ({ sizes, loading }) => {
  const { size, world, plot: plotName } = useParams<{ size: string; world: string; plot: string }>();
  const plotRef = useMemo<PlotRef | null>(
    () => (size && world && plotName ? { size, world, plot: plotName } : null),
    [size, world, plotName]
  );
  const group = sizes?.find((g) => g.size === size);
  const plot = group?.plots.find((p) => p.world === world && p.plot === plotName);
  if (!plot && (sizes == null || loading)) {
    return (
      <div className="main2">
        <div className="center" style={{ paddingTop: 120 }}>
          <span className="spin" /> Loading…
        </div>
      </div>
    );
  }
  if (!plot || !plotRef) return <NoWorld notFound kind="plot" detail={group?.error} />;
  return <PlotPage key={`${size}/${world}/${plotName}`} plotRef={plotRef} plot={plot} />;
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, toggleTheme] = useTheme();
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [worlds, setWorlds] = useState<World[]>([]);
  const [worldsLoading, setWorldsLoading] = useState(false);
  // null until the "Your plots" pages first need the list; it asks every plot server, so it isn't loaded up front.
  const [plots, setPlots] = useState<PlotSizeGroup[] | null>(null);
  const [plotsLoading, setPlotsLoading] = useState(false);
  const [plotsError, setPlotsError] = useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const logoutCleanup = useCallback(() => {
    setAuthUser(null);
    setWorlds([]);
    setPlots(null);
    navigate('/');
  }, [navigate]);
  const logoutRef = useRef(logoutCleanup);
  logoutRef.current = logoutCleanup;

  // 401 → try /auth/refresh once, then retry; otherwise drop to the login screen.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      async (error) => {
        const original = error.config || {};
        if (original.url?.includes('/auth/refresh') || original.url?.includes('/auth/me')) return Promise.reject(error);
        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;
          try {
            const res = await api.post('/auth/refresh');
            if (res.status === 200) {
              if (res.data?.user) {
                setAuthUser({
                  user: res.data.user,
                  minecraftUuid: res.data.minecraftUuid ?? null,
                  isStaff: !!res.data.isStaff,
                  isAdmin: !!res.data.isAdmin,
                  isDeveloper: !!res.data.isDeveloper,
                  isVIP: !!res.data.isVIP,
                });
              }
              return api.request(original);
            }
          } catch (e) {
            console.log('Token refresh failed', e);
          }
          logoutRef.current();
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      setAuthUser(await Auth.me());
    } catch {
      setAuthUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loadWorlds = useCallback(async (background = false) => {
    if (!background) setWorldsLoading(true);
    try {
      setWorlds(await Worlds.all());
    } catch (e) {
      // 403 = no linked Minecraft account for a non-staff user; the rail explains that.
      console.error('Failed to load worlds', e);
      setWorlds([]);
    } finally {
      if (!background) setWorldsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authUser) loadWorlds();
  }, [authUser, loadWorlds]);

  useEffect(() => {
    if (!authUser) return;
    const onFocus = () => loadWorlds(true);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [authUser, loadWorlds]);

  const loadPlots = useCallback(async (background = false) => {
    if (!background) setPlotsLoading(true);
    try {
      setPlots(await Plots.all());
      setPlotsError(null);
    } catch (e) {
      // 403 = no linked Minecraft account; the page explains it. Keep whatever was shown before.
      setPlotsError(errorMessage(e, 'Could not load your plots right now.'));
      setPlots((prev) => prev ?? []);
    } finally {
      if (!background) setPlotsLoading(false);
    }
  }, []);

  const onPlots = location.pathname.startsWith('/plots');
  useEffect(() => {
    if (authUser && onPlots && plots == null && !plotsLoading) loadPlots();
  }, [authUser, onPlots, plots, plotsLoading, loadPlots]);

  useEffect(() => {
    if (!authUser || !onPlots) return;
    const onFocus = () => loadPlots(true);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [authUser, onPlots, loadPlots]);

  const handleLogout = async () => {
    try {
      await Auth.logout();
    } catch (e) {
      console.error('Logout failed', e);
    } finally {
      logoutCleanup();
    }
  };

  const handleDevLogin = async () => {
    setLoginError(null);
    try {
      await Auth.devLogin();
      await checkAuth();
    } catch (e) {
      setLoginError(errorMessage(e, 'Dev login failed. Is the control service running in development mode?'));
    }
  };

  if (!authChecked) {
    return (
      <div className="splash">
        <span className="spin" />
      </div>
    );
  }

  if (!authUser) {
    return <Login onDiscordLogin={() => (window.location.href = Auth.discordLoginUrl)} onDevLogin={isDev ? handleDevLogin : undefined} error={loginError} />;
  }

  const path = location.pathname;
  const view: RailView = path.startsWith('/admin') ? 'admin' : path.startsWith('/worlds') ? 'worlds' : onPlots ? 'plots' : 'home';
  const selectedId = path.startsWith('/worlds/') ? path.split('/').filter(Boolean)[1] || null : null;
  const goWorld = (id: string) => navigate(`/worlds/${id}/settings`);
  const goPlot = (ref: PlotRef) => navigate(plotUrl(ref));
  const refreshInBackground = () => loadWorlds(true);

  return (
    <>
      <div className="shell">
        <Rail
          me={authUser}
          worlds={worlds}
          loading={worldsLoading}
          view={view}
          selectedId={selectedId}
          onHome={() => navigate('/')}
          onPlots={() => navigate('/plots')}
          onAdmin={() => navigate('/admin')}
          onSelect={goWorld}
          onLogout={handleLogout}
          onFeedback={() => setFeedbackOpen(true)}
          theme={theme}
          onToggleTheme={toggleTheme}
        />
        <Routes>
          <Route path="/" element={<Home me={authUser} worlds={worlds} worldsLoading={worldsLoading} onSelect={goWorld} />} />
          <Route path="/worlds" element={<NoWorld />} />
          <Route path="/worlds/:worldId/:tab?" element={<WorldRoute worlds={worlds} loading={worldsLoading} me={authUser} onWorldsRefresh={refreshInBackground} />} />
          <Route
            path="/plots"
            element={<PlotsPage me={authUser} sizes={plots} loading={plotsLoading} error={plotsError} onRefresh={() => loadPlots()} onSelect={goPlot} />}
          />
          <Route path="/plots/:size/:world/:plot/:tab?" element={<PlotRoute sizes={plots} loading={plotsLoading} />} />
          <Route path="/admin" element={<Admin me={authUser} worlds={worlds} loading={worldsLoading} onRefresh={() => loadWorlds(true)} onSelect={goWorld} />} />
          <Route path="/all-worlds" element={<Navigate to="/admin" replace />} />
          <Route path="/system-overview" element={<Navigate to="/admin" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        worlds={isStaffUser(authUser) ? worlds : worlds.filter((w) => w.ownerId === authUser.minecraftUuid)}
        defaultWorldId={selectedId}
      />
    </>
  );
}

const App: React.FC = () => (
  <BrowserRouter>
    <UsernamesProvider>
      <AppContent />
    </UsernamesProvider>
  </BrowserRouter>
);

export default App;
